import { GCGR } from "../interfaces/GCGRs";
import { fetchDataStrict } from "../clients/api";
import { handleError } from "../errors";
import { Result } from "./types";
import { CacheHandle, clearTogether, defineCachedLookup, defineCachedResource } from "./helpers";
import { employeeCaches, fetchEmployees, fetchPositions } from "./admin";

type GCGRScope = { projectId: string; gcgrId: string };

/**
 * Uncached list fetch — the probe path must hit the server fresh to detect changes (a cached probe
 * could never see them).
 *
 * `activeOnly` is the split the two list reads are built on: `__GCGR` holds active GCGRs because
 * user formulas address that table by row. Everything that resolves a *selection* wants the
 * unfiltered set, since an inactive GCGR is selectable.
 */
const fetchGCGRsByProjectIdUncached = async (
  projectId: string,
  { activeOnly }: { activeOnly: boolean }
): Promise<Result<GCGR[]>> => {
  try {
    const params: Record<string, string | number | string[]> = {
      projectId,
      search: activeOnly ? [`project;${projectId}`, "AND", "active;true"] : [`project;${projectId}`],
    };
    const response = await fetchDataStrict({
      table: "projects",
      id: projectId,
      subRoute: "gcgrs",
      params,
    });
    return { success: true, data: response?.data || [] };
  } catch (error) {
    const err = handleError(error, { table: "projects", subRoute: "gcgrs", context: "fetchGCGRsByProjectIdUncached" });
    return { success: false, error: err };
  }
};

/**
 * Active GCGRs only, fetched fresh. Feeds the `__GCGR` table in pre-migration workbooks and the
 * wright-ryan report that mirrors it.
 */
export const fetchGCGRsByProjectId = (projectId: string): Promise<Result<GCGR[]>> =>
  fetchGCGRsByProjectIdUncached(projectId, { activeOnly: true });

const fetchGCGRExportUncached = async ({ projectId, gcgrId }: GCGRScope): Promise<Result<GCGR>> => {
  try {
    const response = await fetchDataStrict({
      table: "projects",
      id: `${projectId}/gcgrs/${gcgrId}`,
      subRoute: "export",
      params: { format: "json" },
      method: "POST",
    });
    return { success: true, data: response };
  } catch (error) {
    const err = handleError(error, {
      table: "projects",
      subRoute: "gcgrs",
      gcgrId,
      context: "fetchGCGRExportUncached",
    });
    return { success: false, error: err };
  }
};

const gcgrExport = defineCachedResource<GCGRScope, GCGR>({
  scopeKey: ({ gcgrId }) => gcgrId,
  fetch: fetchGCGRExportUncached,
});

/**
 * The GCGR record, read from its export, which carries the record's own fields. A GCGR the project
 * no longer has returns success with no data, the way a pick tells gone.
 */
const fetchGCGRRecordUncached = async (scope: GCGRScope): Promise<Result<GCGR>> => {
  const result = await fetchGCGRExportUncached(scope);
  if (result.success) return result;
  // A failed export carries no status, so only the project's list can tell a deleted GCGR from a
  // failed request.
  const listed = await fetchGCGRsByProjectIdUncached(scope.projectId, { activeOnly: false });
  if (listed.success && !(listed.data ?? []).some((gcgr) => gcgr.id === scope.gcgrId)) {
    return { success: true, data: undefined };
  }
  return result;
};

const selectedGcgr = defineCachedResource<GCGRScope, GCGR>({
  scopeKey: ({ gcgrId }) => gcgrId,
  fetch: fetchGCGRRecordUncached,
});

export const fetchSelectedGCGR = (projectId: string, gcgrId: string): Promise<Result<GCGR>> =>
  selectedGcgr.get({ projectId, gcgrId });

export const clearGCGRCaches = (): void => clearTogether(gcgrCaches);

/**
 * Fetch the selected GCGR's export fresh, which carries the GCGR record too, and swap both into the
 * caches on success; on failure the existing entries are left intact as the fallback (see
 * refreshEstimateExportCache). A failed export fails the refresh and leaves everything as it was;
 * a successful one also reloads the lookups (employees, positions, detail groups, labor types),
 * which don't fail the refresh. A successful empty result means the project no longer has it. A
 * clear while it runs leaves both caches cleared. With `awaitLookups` false it returns once the
 * payload is cached, and the lookups finish in the background; reads of them wait on the requests
 * already running.
 */
export const refreshGCGRCaches = async (
  projectId: string,
  gcgrId: string,
  { awaitLookups = true }: { awaitLookups?: boolean } = {}
): Promise<Result<GCGR>> => {
  const notCleared = selectedGcgr.watchForClear();
  const exportResult = await fetchGCGRRecordUncached({ projectId, gcgrId });
  if (!exportResult.success || !exportResult.data) return exportResult;
  gcgrExport.stage({ projectId, gcgrId }, exportResult.data, notCleared);
  const staged = selectedGcgr.stage({ projectId, gcgrId }, exportResult.data, notCleared);
  // A clear while this ran belongs to a newer selection, whose lookups this must not drop.
  if (notCleared()) {
    clearGCGRLookupCaches();
    const lookups = loadGCGRLookups();
    if (awaitLookups) await lookups;
  }
  return staged;
};

/**
 * Full GCGR export payload (staff details, phases, line items, …) — memoized because a single
 * report run touches it from several paths (refresh, generation, status checks).
 *
 * Cached: memoized per gcgrId. Invalidate via clearGCGRCaches().
 */
const fetchGCGRExport = (projectId: string, gcgrId: string): Promise<Result<GCGR>> =>
  gcgrExport.get({ projectId, gcgrId });

const fetchGCGRDetailGroupsUncached = async (): Promise<Result<Array<Record<string, any>>>> => {
  try {
    const response = await fetchDataStrict({ table: "gcgr_detail_groups" });
    const groups = response?.data || [];
    return { success: true, data: groups };
  } catch (error) {
    const err = handleError(error, { table: "gcgr_detail_groups", context: "fetchGCGRDetailGroups" });
    return { success: false, error: err };
  }
};

const fetchGCGRNonstaffLaborTypesUncached = async (): Promise<Result<Array<Record<string, any>>>> => {
  try {
    const response = await fetchDataStrict({ table: "gcgr_nonstaff_labor_types" });
    const laborTypes = response?.data || [];
    return { success: true, data: laborTypes };
  } catch (error) {
    const err = handleError(error, { table: "gcgr_nonstaff_labor_types", context: "fetchGCGRNonstaffLaborTypes" });
    return { success: false, error: err };
  }
};

const detailGroups = defineCachedLookup(fetchGCGRDetailGroupsUncached);
const nonstaffLaborTypes = defineCachedLookup(fetchGCGRNonstaffLaborTypesUncached);

const fetchGCGRDetailGroups = detailGroups.get;

const gcgrLookupCaches: CacheHandle[] = [detailGroups, nonstaffLaborTypes, ...employeeCaches];

/** Everything the GCGR caches, lookups included. */
export const gcgrCaches: CacheHandle[] = [selectedGcgr, gcgrExport, ...gcgrLookupCaches];

const clearGCGRLookupCaches = (): void => clearTogether(gcgrLookupCaches);
const fetchGCGRNonstaffLaborTypes = nonstaffLaborTypes.get;

const loadGCGRLookups = async (): Promise<void> => {
  const loads: Promise<unknown>[] = [detailGroups.get(), nonstaffLaborTypes.get(), fetchEmployees(), fetchPositions()];
  await Promise.all(loads.map((load) => load.catch(() => undefined)));
};

export {
  fetchGCGRsByProjectIdUncached,
  fetchGCGRExport,
  fetchGCGRDetailGroups,
  fetchGCGRDetailGroupsUncached,
  fetchGCGRNonstaffLaborTypes,
};
